import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { FaCaretLeft, FaCog, FaDiscord, FaLink, FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import { UNLINK_USER_CHATURBATE } from "../../../queries/mutation/broadcaster/queries";
import {
  GET_USER_BROADCASTER_CONFIG,
  UPDATE_USER_BROADCASTER_CONFIG,
} from "../../../queries/mutation/userBroadcasterConfig";
import { ReinUser } from "../../../types/ReinUser";
import CrimcoinAdjuster from "../../CrimcoinAdjuster";

type Props = {
  reinUser: ReinUser;
  refreshTipper: () => void;
  clearUser: () => void;
};
const ReinUserProfile = (props: Props) => {
  const { reinUser, refreshTipper, clearUser } = props;
  const [confirmingCbUnlink, setConfirmingCbUnlink] = useState(false);

  // Fetch user broadcaster config
  const { data: configData, refetch: refetchConfig } = useQuery(
    GET_USER_BROADCASTER_CONFIG,
    {
      variables: { userId: reinUser.id },
      skip: !reinUser.id,
      fetchPolicy: "network-only",
    },
  );

  const handleError = (error: ApolloError) => {
    toast.error(error.message);
  };

  // Update user broadcaster config
  const [updateConfig] = useMutation(UPDATE_USER_BROADCASTER_CONFIG, {
    onError: handleError,
    onCompleted: () => {
      toast.success("Config updated successfully");
      refetchConfig();
    },
  });

  // The returned row replaces this user's in the cached list, so the box
  // and the list update on their own.
  const [unlinkChaturbate, { loading: unlinkingCb }] = useMutation(
    UNLINK_USER_CHATURBATE,
    { onError: handleError },
  );
  const confirmCbUnlink = async () => {
    // Read before the mutation lands, since the row's cbUsername goes null.
    const cbName = reinUser.cbUsername;
    const { data } = await unlinkChaturbate({ variables: { userId: reinUser.id } });
    if (data) {
      toast.success(`Unlinked ${cbName} from ${reinUser.username}`);
      setConfirmingCbUnlink(false);
    }
  };

  const handleTogglePermission = async (permission: string, value: boolean) => {
    await updateConfig({
      variables: {
        input: {
          userId: reinUser.id,
          permissions: {
            [permission]: value,
          },
        },
      },
    });
  };

  const config = configData?.getUserBroadcasterConfig;
  return (
    <div className="bg-base-200 h-full flex flex-col gap-2 items-center p-2 animate-fade-in">
      <div className="card bg-base-100 w-full sm:w-6/12 overflow-auto flex">
        <div className="card-body p-2 sm:p-4 overflow-auto flex flex-col overflow-y-auto">
          <div className="card-title justify-between flex flex-row">
            <div className="flex flex-row items-center gap-2 flex-wrap">
              <div className="btn btn-ghost btn-sm" onClick={clearUser}>
                <FaCaretLeft size={30} />
              </div>
              Rein Profile:
              <div className="inline text-primary">{reinUser.username}</div>
            </div>
            <div className="text-primary">
              <FaUser size={24} />
            </div>
          </div>
          <div className="divider divider-primary m-0" />
          <div className="flex flex-col flex-grow overflow-y-auto gap-2">
            <div
              className={`rounded-box bg-base-300 p-2 border-2 border-primary ${
                reinUser.discordProfile ? "text-primary" : ""
              }`}
            >
              <div className="flex flex-row justify-between">
                Discord
                <FaDiscord size={30} />
              </div>
              <div className="divider m-0 divider-primary" />

              {reinUser.discordProfile ? (
                <div>User is linked with discord</div>
              ) : (
                <div className="text-error text-center">
                  No Discord Profile Sync'd
                </div>
              )}
            </div>
            <div
              className={`rounded-box bg-base-300 p-2 border-2 border-warning ${
                reinUser.cbUsername ? "text-warning" : ""
              }`}
            >
              <div className="flex flex-row justify-between">
                Chaturbate
                <FaLink size={30} />
              </div>
              <div className="divider m-0 divider-warning" />
              {reinUser.cbUsername ? (
                <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                  <div className="break-all">
                    {reinUser.cbUsername}
                    {reinUser.inFanclub && (
                      <span className="badge badge-success badge-xs ml-2 align-middle">
                        fan club
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => setConfirmingCbUnlink(true)}
                  >
                    Unlink
                  </button>
                </div>
              ) : (
                <div className="text-error text-center">
                  No Chaturbate account linked
                </div>
              )}
            </div>
            <CrimcoinAdjuster
              userId={reinUser.id}
              username={reinUser.username}
              balance={reinUser.discordTokens}
              onAdjusted={refreshTipper}
            />

            {/* User Configuration */}
            <div className="rounded-box bg-base-300 p-2 border-2 border-primary text-primary">
              <div className="flex flex-row justify-between">
                User Configuration
                <FaCog size={30} />
              </div>
              <div className="divider m-0 divider-primary" />
              {config ? (
                <div className="space-y-3">
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-3">
                      <input
                        type="checkbox"
                        className="toggle toggle-success"
                        checked={config.permissions.unlimitedHeadpats}
                        onChange={(e) =>
                          handleTogglePermission(
                            "unlimitedHeadpats",
                            e.target.checked,
                          )
                        }
                      />
                      <div>
                        <div className="font-semibold">Unlimited Headpats</div>
                        <div className="text-sm text-gray-400">
                          Allow this user to use headpats without limits
                        </div>
                      </div>
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-3">
                      <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={config.permissions.isMod}
                        onChange={(e) =>
                          handleTogglePermission("isMod", e.target.checked)
                        }
                      />
                      <div>
                        <div className="font-semibold">Moderator</div>
                        <div className="text-sm text-gray-400">
                          Grant moderator privileges to this user
                        </div>
                      </div>
                    </label>
                  </div>
                  <div className="text-xs text-gray-500 pt-2">
                    Last updated:{" "}
                    {new Date(config.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="text-center">Loading config...</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {confirmingCbUnlink && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              Unlink {reinUser.cbUsername} from {reinUser.username}?
            </h3>
            <p className="py-4 text-sm">
              They linked this Chaturbate account themselves. Fan club VIP from
              it stops counting for them now, and they can link a Chaturbate
              account again on Link Accounts whenever they're ready. Their
              crimcoins, subscriptions and any VIP you granted stay on their
              Reinstorm account. They'll get a Discord DM about it.
            </p>
            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => setConfirmingCbUnlink(false)}
                disabled={unlinkingCb}
              >
                Keep linked
              </button>
              <button
                className="btn btn-error"
                onClick={confirmCbUnlink}
                disabled={unlinkingCb}
              >
                {unlinkingCb ? "Unlinking..." : "Unlink"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ReinUserProfile;

{
  /* */
}
